// Copyright (c) Mysten Labs, Inc.
// Modifications Copyright (c) 2024 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

import { DisplayStats, TooltipPosition } from '@iota/apps-ui-kit';
import {
    capitalize,
    CoinFiatValue,
    CoinIcon,
    ImageIconSize,
    resolveNFTMedia,
    useCoinMetadata,
    useFormatCoin,
    useNFTMediaHeaders,
} from '@iota/core';
import { type IotaObjectResponse, type ObjectOwner } from '@iota/iota-sdk/client';
import { CoinFormat, formatDigest, parseStructTag } from '@iota/iota-sdk/utils';
import { SortByDefault } from '@iota/apps-ui-icons';
import clsx from 'clsx';
import { useState } from 'react';
import { OwnerDisplay } from '~/components/object';
import {
    Link,
    LinkWithQuery,
    ObjectLink,
    ObjectVideoImage,
    TransactionLink,
} from '~/components/ui';
import {
    extractName,
    getCoinPagePath,
    getCoinTypeOfObject,
    onCopySuccess,
    parseObjectType,
    trimStdLibPrefix,
    truncateStruct,
} from '~/lib/utils';

interface HeroVideoImageProps {
    title: string;
    subtitle: string;
    src: string;
}

function HeroVideoImage({ title, subtitle, src }: HeroVideoImageProps): JSX.Element {
    const [open, setOpen] = useState(false);

    return (
        <div className="group relative h-full">
            <ObjectVideoImage
                objectFit="contain"
                title={title}
                subtitle={subtitle}
                src={src}
                variant="fill"
                open={open}
                setOpen={setOpen}
                rounded="xl"
                disableVideoControls
            />
            <Link
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute right-3 top-3 hidden h-8 w-8 items-center justify-center rounded-md bg-white/40 backdrop-blur group-hover:flex"
            >
                <SortByDefault className="h-4 w-4 rotate-45" />
            </Link>
        </div>
    );
}

interface NameCardProps {
    name: string;
}

function NameCard({ name }: NameCardProps): JSX.Element {
    return <DisplayStats label="Name" value={name} />;
}

interface DescriptionCardProps {
    display?: {
        [key: string]: string;
    };
}

function DescriptionCard({ display }: DescriptionCardProps): JSX.Element {
    return <DisplayStats label="Description" value={display?.description ?? ''} />;
}

interface ObjectIdCardProps {
    objectId: string;
}

function ObjectIdCard({ objectId }: ObjectIdCardProps): JSX.Element {
    return (
        <DisplayStats
            label="Object ID"
            tooltipText={OBJECT_FIELD_TOOLTIP.objectId}
            tooltipPosition={TooltipPosition.Top}
            value={
                <div className="flex flex-col gap-xs">
                    <ObjectLink objectId={objectId} copyText={objectId} />
                </div>
            }
        />
    );
}

interface TypeCardCardProps {
    objectType: string;
}

function TypeCard({ objectType }: TypeCardCardProps): JSX.Element {
    const { address, module } = parseStructTag(objectType);
    const normalizedStructTag = truncateStruct(objectType);
    return (
        <DisplayStats
            label="Type"
            value={
                <ObjectLink
                    objectId={`${address}?module=${module}`}
                    label={normalizedStructTag}
                    showAddressAlias={false}
                >
                    {normalizedStructTag}
                </ObjectLink>
            }
            tooltipText={objectType}
            tooltipPosition={TooltipPosition.Top}
            copyText={objectType}
            onCopySuccess={onCopySuccess}
        />
    );
}

interface CoinCardProps {
    coinType: string;
}

function CoinCard({ coinType }: CoinCardProps): JSX.Element {
    const { data: coinMetadata } = useCoinMetadata(coinType, false);
    return (
        <DisplayStats
            label="Coin"
            tooltipText="The coin this object belongs to."
            tooltipPosition={TooltipPosition.Top}
            value={
                <LinkWithQuery
                    to={getCoinPagePath(coinType)}
                    className="inline-flex items-center gap-x-xxs text-iota-primary-30 dark:text-iota-primary-80"
                >
                    <CoinIcon coinType={coinType} size={ImageIconSize.Small} />
                    {coinMetadata?.name}
                </LinkWithQuery>
            }
        />
    );
}

interface CoinBalanceCardProps {
    coinType: string;
    balance: string;
}

function CoinBalanceCard({ coinType, balance }: CoinBalanceCardProps): JSX.Element {
    const [formattedBalance, symbol] = useFormatCoin({
        balance,
        coinType,
        format: CoinFormat.Full,
        truncate: false,
    });
    return (
        <DisplayStats
            label="Balance"
            tooltipText="The amount of the coin held in this object."
            tooltipPosition={TooltipPosition.Top}
            value={
                <div className="flex flex-col gap-xxs">
                    <div className="flex flex-row flex-wrap items-baseline gap-xxs">
                        <span className="break-all">{formattedBalance}</span>
                        <span className="whitespace-nowrap text-label-md opacity-40">{symbol}</span>
                    </div>
                    <CoinFiatValue amount={balance} coinType={coinType} withParentheses={false} />
                </div>
            }
        />
    );
}

interface VersionCardProps {
    version?: string;
}

function VersionCard({ version }: VersionCardProps): JSX.Element {
    return (
        <DisplayStats
            label="Version"
            tooltipText={OBJECT_FIELD_TOOLTIP.version}
            tooltipPosition={TooltipPosition.Top}
            value={version ?? '--'}
        />
    );
}

interface LastTxBlockCardProps {
    digest: string;
}

function LastTxBlockCard({ digest }: LastTxBlockCardProps): JSX.Element {
    return (
        <DisplayStats
            label="Last Transaction Block Digest"
            tooltipText={OBJECT_FIELD_TOOLTIP.lastTransaction}
            tooltipPosition={TooltipPosition.Top}
            value={<TransactionLink digest={digest}>{formatDigest(digest)}</TransactionLink>}
            copyText={digest}
            onCopySuccess={onCopySuccess}
        />
    );
}

interface DigestCardProps {
    digest: string;
}

function DigestCard({ digest }: DigestCardProps): JSX.Element {
    return (
        <DisplayStats
            label="Object Digest"
            tooltipText={OBJECT_FIELD_TOOLTIP.digest}
            tooltipPosition={TooltipPosition.Top}
            value={formatDigest(digest)}
            copyText={digest}
            onCopySuccess={onCopySuccess}
        />
    );
}

interface OwnerCardProps {
    objOwner: ObjectOwner;
}

function OwnerCard({ objOwner }: OwnerCardProps): JSX.Element | null {
    return (
        <DisplayStats
            label="Owner"
            tooltipText={OBJECT_FIELD_TOOLTIP.owner}
            tooltipPosition={TooltipPosition.Top}
            value={
                <div className="flex w-full min-w-0 flex-col gap-xs">
                    <OwnerDisplay objOwner={objOwner} />
                </div>
            }
        />
    );
}

interface StorageRebateCardProps {
    storageRebate: string;
}

function StorageRebateCard({ storageRebate }: StorageRebateCardProps): JSX.Element | null {
    const [storageRebateFormatted, symbol] = useFormatCoin({
        balance: storageRebate,
        format: CoinFormat.Full,
    });

    return (
        <DisplayStats
            label="Storage Rebate"
            tooltipText={OBJECT_FIELD_TOOLTIP.storageRebate}
            tooltipPosition={TooltipPosition.Top}
            value={
                <div className="flex min-w-0 flex-col gap-xxs">
                    <div className="flex flex-row flex-wrap items-baseline gap-xxs">
                        <span className="break-all">{storageRebateFormatted}</span>
                        <span className="whitespace-nowrap break-normal text-label-md opacity-40">
                            {symbol}
                        </span>
                    </div>
                    <CoinFiatValue amount={storageRebate} withParentheses={false} />
                </div>
            }
        />
    );
}

interface ObjectViewProps {
    data: IotaObjectResponse;
}

export const OBJECT_FIELD_TOOLTIP = {
    objectId:
        'The unique identifier of this object. It is assigned when the object is created and never changes, even if the object is transferred or updated.',
    version:
        'Every change to an object raises its version number. The numbers are not consecutive, so gaps between versions are normal.',
    digest: 'A fingerprint of this exact version of the object. Any change to its contents produces a different one.',
    owner: 'Determines who can use this object: a single address, anyone at all if it is shared, or nobody if it is immutable and can no longer change. Objects can also be held inside another object.',
    lastTransaction: 'The most recent transaction that changed this object.',
    publisher: 'The address that sent the transaction publishing this package.',
    published: 'When this version of the package was published on the network.',
    storageRebate:
        'IOTA held as a storage deposit for this object. The amount is returned when the object is deleted or its storage usage decreases.',
};

export function ObjectView({ data }: ObjectViewProps): JSX.Element {
    const display = data.data?.display?.data;
    const src = display?.image_url || '';
    const { data: nftMediaHeaders } = useNFTMediaHeaders(src);
    const { type: nftFileType } = resolveNFTMedia(src, nftMediaHeaders);

    const name = extractName(display);
    const objectType = parseObjectType(data);
    const objOwner = data.data?.owner;
    const storageRebate = data.data?.storageRebate;
    const objectId = data.data?.objectId;
    const lastTransactionBlockDigest = data.data?.previousTransaction;
    const objectDigest = data.data?.digest;
    const coinType = getCoinTypeOfObject(objectType);
    const content = data.data?.content;
    const coinBalance =
        coinType &&
        content?.dataType === 'moveObject' &&
        parseStructTag(objectType).name === 'Coin' &&
        typeof (content.fields as { balance?: unknown }).balance === 'string'
            ? (content.fields as { balance: string }).balance
            : null;

    const heroImageTitle = name || display?.description || trimStdLibPrefix(objectType);
    const heroImageSubtitle = `1 ${capitalize(nftFileType)} File`;
    const heroImageProps = {
        title: heroImageTitle,
        subtitle: heroImageSubtitle,
        src,
    };

    return (
        <div className="flex flex-col gap-md">
            <div
                className={clsx(
                    'address-grid-container-top',
                    !src && 'no-image',
                    (!name || !display) && 'no-description',
                )}
            >
                {src && (
                    <div style={{ gridArea: 'heroImage' }}>
                        <HeroVideoImage {...heroImageProps} />
                    </div>
                )}
                {name && (
                    <div style={{ gridArea: 'name' }}>
                        <NameCard name={name} />
                    </div>
                )}
                {display?.description && (
                    <div style={{ gridArea: 'description' }}>
                        <DescriptionCard display={display} />
                    </div>
                )}

                {objectId && (
                    <div style={{ gridArea: 'objectId' }}>
                        <ObjectIdCard objectId={objectId} />
                    </div>
                )}

                {objectType && objectType !== 'unknown' && (
                    <div style={{ gridArea: 'type' }}>
                        <TypeCard objectType={objectType} />
                    </div>
                )}

                {data.data?.version && (
                    <div style={{ gridArea: 'version' }}>
                        <VersionCard version={data.data?.version} />
                    </div>
                )}
                {lastTransactionBlockDigest && (
                    <div style={{ gridArea: 'lastTxBlock' }}>
                        <LastTxBlockCard digest={lastTransactionBlockDigest} />
                    </div>
                )}
                {objOwner && (
                    <div style={{ gridArea: 'owner' }}>
                        <OwnerCard objOwner={objOwner} />
                    </div>
                )}
                {storageRebate && (
                    <div style={{ gridArea: 'storageRebate' }}>
                        <StorageRebateCard storageRebate={storageRebate} />
                    </div>
                )}
                {objectDigest && (
                    <div style={{ gridArea: 'digest' }}>
                        <DigestCard digest={objectDigest} />
                    </div>
                )}
            </div>
            {coinType && (
                <div className="flex flex-col gap-md md:flex-row">
                    <div className="flex-1">
                        <CoinCard coinType={coinType} />
                    </div>
                    {coinBalance && (
                        <div className="flex-1">
                            <CoinBalanceCard coinType={coinType} balance={coinBalance} />
                        </div>
                    )}
                </div>
            )}
            <div className="flex flex-row gap-md">
                {display && display.link && (
                    <div className="flex-1">
                        <DisplayStats
                            label="Link"
                            value={
                                <Link
                                    variant="mono"
                                    href={display.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {display.link}
                                </Link>
                            }
                        />
                    </div>
                )}
                {display && display.project_url && (
                    <div className="flex-1">
                        <DisplayStats
                            label="Website"
                            value={
                                <Link
                                    variant="mono"
                                    href={display.project_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {display.project_url}
                                </Link>
                            }
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
